import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, Loader2, AlertCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	PeopleSearchCategory,
	criteriaChips,
} from "@/types/peopleSearch";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";
import { usePeopleSearchCount } from "@/hooks/peopleSearch/usePeopleSearchCount";
import { useLists } from "@/hooks/lists/useLists";

const MAX_VISIBLE_CHIPS = 2;

export function SummaryBar() {
	const { t } = useTranslation();
	const { draft, applied, unappliedCount, clearCategory, apply } = usePeopleSearch();
	const { count, isCounting, countError, refetchCount } = usePeopleSearchCount(draft);
	const { data: lists } = useLists();

	const chipGroups = useMemo(() => {
		const chips = criteriaChips(applied);
		// List criteria store ids — map them to names for display
		if (chips.lists && lists)
			chips.lists = chips.lists.map(id => lists.find(l => l.id === id)?.name || id);
		return chips;
	}, [applied, lists]);

	const groupEntries = Object.entries(chipGroups) as [PeopleSearchCategory, string[]][];

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			toast.success(t("peopleSearch.summary.linkCopied", "Search link copied"));
		} catch {
			toast.error(t("peopleSearch.summary.linkCopyFailed", "Couldn't copy the link"));
		}
	};

	return (
		<div className="border-t bg-background pl-4 pr-24 py-3 flex items-center gap-3 flex-wrap">
			<div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
				{groupEntries.map(([category, chips]) => (
					<div key={category} className="flex items-center gap-1 border rounded-sm px-2 py-1">
						<span className="text-xs font-medium text-muted-foreground">
							{t(`peopleSearch.categories.${category}`, category)}:
						</span>
						{chips.slice(0, MAX_VISIBLE_CHIPS).map(chip => (
							<Badge key={chip} variant="secondary" className="text-xs font-normal">
								{chip}
							</Badge>
						))}
						{chips.length > MAX_VISIBLE_CHIPS && (
							<Badge variant="secondary" className="text-xs font-normal">
								+{chips.length - MAX_VISIBLE_CHIPS}
							</Badge>
						)}
						<button
							type="button"
							onClick={() => clearCategory(category)}
							className="rounded-sm hover:bg-muted p-0.5 ml-0.5"
							aria-label={t("peopleSearch.summary.clearCategory", "Clear {{category}} filters", {
								category: t(`peopleSearch.categories.${category}`, category),
							})}
						>
							<X className="h-3 w-3" />
						</button>
					</div>
				))}
			</div>
			<div className="flex items-center gap-3 shrink-0">
				{groupEntries.length > 0 && (
					<Button
						variant="ghost"
						size="sm"
						onClick={copyLink}
						className="text-muted-foreground"
					>
						<Link2 className="h-4 w-4 mr-1" />
						{t("peopleSearch.summary.copyLink", "Copy link")}
					</Button>
				)}
				{unappliedCount > 0 && (
					<span className="text-xs text-amber-600 dark:text-amber-400">
						{t("peopleSearch.summary.unappliedChanges", "{{count}} unapplied changes", { count: unappliedCount })}
					</span>
				)}
				{countError && !isCounting ? (
					<button
						type="button"
						onClick={() => refetchCount()}
						className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
						title={t("peopleSearch.summary.countErrorRetry", "Retry live count")}
					>
						<AlertCircle className="h-3.5 w-3.5" />
						{t("peopleSearch.summary.countError", "Live count unavailable")}
					</button>
				) : null}
				<Button onClick={apply}>
					{isCounting ? (
						<Loader2 className="h-4 w-4 mr-2 animate-spin" />
					) : null}
					<span aria-live="polite">
						{count !== null && !isCounting
							? t("peopleSearch.summary.showProfiles", "Show {{count}} profiles", { count })
							: t("peopleSearch.summary.showProfilesLoading", "Show profiles")}
					</span>
				</Button>
			</div>
		</div>
	);
}
